/**
 * client/camera/camera.ts — refdef: eye position, view angles, FOV.
 *
 * OWNER: VIEWMODEL+CAMERA agent.
 * Port of the T5 first-person view path (KisakBlack cgame_mp/cg_view_mp.cpp CG_CalcViewValues ->
 * CG_SmoothCameraZ -> CG_OffsetFirstPersonView -> CG_ShakeCamera -> CG_CalcFov), see dvars.ts for every
 * constant. Order per frame:
 *   eye = view.origin + viewHeight (interpolated, smoothed prediction)          [ARCH §4]
 *   + step smoothing (PEv.STEP)                                                 CG_SmoothCameraZ
 *   angles += damage flinch + ADS view bob                                     BG_CalculateViewMovementAngles
 *   eye.z += vertical bob, eye += horizontal bob * right                        [MV §1.5]
 *   eye.z += landing dip (PEv.LAND)                                             150 ms down / 300 ms up
 *   eye.z >= origin.z + 8
 *   angles += fire view kick (hip/adsViewKick*, CenterSpeed)                   CG_KickAngles
 *   angles += explosion shake (explosionCameraShake* of the weapon, EXPLOSION) CG_ShakeCamera
 *   fov = CG_GetViewFov (cg_fov lerped to adsZoomFov with adsZoomIn/OutFrac)   fov.ts
 * The viewmodel later adds the tag_camera view animation (CG_ApplyViewAnimation) to this refdef.
 *
 * Deviation: in the engine the fire kick is added to the usercmd angles (CL_SetUserCmdAimValues), i.e. it
 * also moves the aim. Here it is visual only until input/cmdgen read it (CONTRACT-REQUEST #V1).
 * Dead: third-person death cam around the corpse facing the killer (CG_OffsetThirdPersonView with the Q3 wall
 * trace, deathcam.ts; ESTIMATE: focus = corpse origin + DEATHCAM_FOCUS_HEIGHT instead of the ragdoll's j_head).
 */
import { CG_FOV, R_ZNEAR } from '@shared/constants';
import { angleVectors, type Vec3 } from '@shared/math';
import { EF, GameEventType, PEv, PmType, PMF, WeaponId, type PlayerState } from '@shared/types';
import { getWeaponDef, type WeaponDef } from '@shared/weapons/defs';
import type { CameraController, ClientWorld, RefDef } from '../types';
import type { CollisionWorld } from '@shared/collision/types';
import { thirdPersonViewTrace } from './deathcam';
import type { Settings } from '../settings/settings';
import { beginPresentationFrame, effectivePs, frameEvents, predictedLerp, rawNum, vmDebug } from '../viewmodel/psview';
import { getBobCycle, viewBobHorizontal, viewBobVertical } from './bob';
import {
  CG_THIRDPERSON_ANGLE, CG_THIRDPERSON_FOCUS_DIST, CG_THIRDPERSON_RANGE, DEATHCAM_FOCUS_HEIGHT,
} from './dvars';
import { cgFovToVertical, updatePositionToAds, viewFov } from './fov';
import { CameraShakes, DamageFlinch, LandDip, StepSmoother, ViewKick, landViewDip, type ViewKickDef } from './viewmove';

export { cgFovToVertical } from './fov';

const kickDefCache = new Map<number, ViewKickDef>();
function kickDef(def: WeaponDef): ViewKickDef {
  let k = kickDefCache.get(def.id);
  if (!k) {
    const n = (s: string) => rawNum(def, s);
    k = {
      hipViewKickPitchMin: n('hipViewKickPitchMin'), hipViewKickPitchMax: n('hipViewKickPitchMax'),
      hipViewKickYawMin: n('hipViewKickYawMin'), hipViewKickYawMax: n('hipViewKickYawMax'),
      adsViewKickPitchMin: n('adsViewKickPitchMin'), adsViewKickPitchMax: n('adsViewKickPitchMax'),
      adsViewKickYawMin: n('adsViewKickYawMin'), adsViewKickYawMax: n('adsViewKickYawMax'),
      hipViewKickCenterSpeed: n('hipViewKickCenterSpeed'), adsViewKickCenterSpeed: n('adsViewKickCenterSpeed'),
    };
    kickDefCache.set(def.id, k);
  }
  return k;
}

/**
 * PEv.LAND parm: surfaceType | severity << 8 | viewDip << 10 (CONTRACT-REQUEST #V2 asks pmove to send the
 * PM_CrashLand viewDip in bits 10..14). Without it: ESTIMATE from severity (light/medium/hard land events at
 * fall heights > 4 / >= 8 / >= 12 [MV §1.5]: dips 0 / 0 / 4, i.e. the smallest dip a hard landing can give).
 */
export function landDipFromParm(parm: number): number {
  const dip = (parm >> 10) & 31;
  if (dip) return dip;
  const severity = (parm >> 8) & 3;
  return severity >= 2 ? landViewDip(12.01) : 0;
}

/** PEv.STEP parm: |dz| in bits 0..14, bit 15 set = step down (CONTRACT-REQUEST #V2). */
export function stepDeltaFromParm(parm: number): number {
  const mag = parm & 0x7fff;
  return parm & 0x8000 ? -mag : mag;
}

export class FirstPersonCamera implements CameraController {
  private readonly ref: RefDef = { origin: [0, 0, 0], angles: [0, 0, 0], fovY: cgFovToVertical(CG_FOV), viewmodelFovY: cgFovToVertical(CG_FOV), zNear: R_ZNEAR, zFar: 60000, adsFrac: 0 };
  readonly kick = new ViewKick();
  readonly shakes = new CameraShakes();
  readonly flinch = new DamageFlinch();
  readonly land = new LandDip();
  readonly step = new StepSmoother();
  private now = 0;
  private positionToAds = true;
  private spawnCount = -1;
  private collision: CollisionWorld | null = null;
  private deadYaw: number | null = null;
  private killer = -1;
  private readonly fwd: Vec3 = [0, 0, 0];
  private readonly right: Vec3 = [0, 0, 0];
  private readonly tmp: Vec3 = [0, 0, 0];

  /** Map collision for the death cam wall check (CameraController.setCollisionWorld, additive). */
  setCollisionWorld(world: CollisionWorld | null): void {
    this.collision = world;
  }

  update(world: ClientWorld, settings: Settings, dtMs: number): RefDef {
    beginPresentationFrame();
    if (!vmDebug.paused) this.now += dtMs;
    const now = this.now;
    const ps = effectivePs(world);
    const r = this.ref;
    const v = world.view;
    predictedLerp.update(ps, Number.isFinite(v.alpha) ? v.alpha : 1); // render-rate bobCycle/velocity (camera + viewmodel)
    r.kickAngles = this.kick.angles; // CONTRACT-REQUEST #V1 (integration): fed into the cmd angles by the app

    if (ps.spawnCount !== this.spawnCount) {
      this.spawnCount = ps.spawnCount;
      this.kick.reset(); this.shakes.clear(); this.step.reset();
      this.flinch.time = 0; this.land.time = -1e9; this.deadYaw = null; this.killer = -1;
    }

    const def = ps.weapon !== WeaponId.NONE ? getWeaponDef(ps.weapon) : null;
    angleVectors(v.viewangles, this.fwd, this.right, null);
    this.events(world, ps, now, v.origin);

    const dead = ps.pm_type === PmType.DEAD || ps.pm_type === PmType.DEAD_LINKED || (ps.eFlags & EF.DEAD) !== 0;
    r.adsFrac = dead ? 0 : ps.adsFrac;
    if (ps.pm_type === PmType.INTERMISSION || ps.pm_type === PmType.SPECTATOR) {
      r.origin[0] = v.origin[0]; r.origin[1] = v.origin[1]; r.origin[2] = v.origin[2] + v.viewHeight;
      r.angles[0] = v.viewangles[0]; r.angles[1] = v.viewangles[1]; r.angles[2] = v.viewangles[2];
    } else if (dead) {
      this.deathCam(world, ps, r);
    } else {
      this.firstPerson(world, ps, def, dtMs, now, r);
    }

    // FOV (CG_CalcFov): no ADS zoom while dead
    this.positionToAds = updatePositionToAds(this.positionToAds, r.adsFrac);
    const fov = viewFov(settings.fov, dead ? null : def, r.adsFrac, this.positionToAds);
    r.fovY = cgFovToVertical(fov);
    // viewmodel uses the same projection as the world (ESTIMATE, models-anims.md §4: no separate viewmodel fov dvar in T6)
    r.viewmodelFovY = r.fovY;
    r.zNear = R_ZNEAR;
    vmDebug.stats.camera = {
      fovX4x3: fov, fovY: r.fovY, kick: [...this.kick.angles], landChange: this.land.change, stepChange: this.step.change,
      flinch: [this.flinch.pitch, this.flinch.roll], dead, origin: [...r.origin], angles: [...r.angles],
    };
    return r;
  }

  private events(world: ClientWorld, ps: PlayerState, now: number, eyeBase: Readonly<Vec3>): void {
    const me = world.localClientNum;
    for (const e of frameEvents(world)) {
      if (e.src === 'pred') {
        switch (e.ev) {
          case PEv.WEAPON_FIRE:
          case PEv.WEAPON_FIRE_LAST: {
            const w = (e.parm & 0xff) as WeaponId;
            if (w !== WeaponId.NONE) this.kick.fire(kickDef(getWeaponDef(w)), ps.adsFrac, Math.random(), Math.random());
            break;
          }
          case PEv.LAND: this.land.land(now, landDipFromParm(e.parm)); break;
          case PEv.STEP: this.step.step(now, stepDeltaFromParm(e.parm)); break;
        }
      } else if (e.src === 'game') {
        const ge = e.ge;
        if (ge.type === GameEventType.EXPLOSION) {
          // explosionCameraShake* of the exploding weapon [WPN §2.2: explosive_bolt_mp 0.5 / 500 ms / 800]
          let wd = getWeaponDef(ge.weapon);
          if (rawNum(wd, 'explosionCameraShakeScale') <= 0 && wd.grenadeWeapon) wd = getWeaponDef(wd.grenadeWeapon);
          const eye = this.tmp;
          eye[0] = eyeBase[0]; eye[1] = eyeBase[1]; eye[2] = eyeBase[2] + world.view.viewHeight;
          this.shakes.start(now, rawNum(wd, 'explosionCameraShakeScale'), rawNum(wd, 'explosionCameraShakeDuration'), ge.pos, rawNum(wd, 'explosionCameraShakeRadius'), eye);
        } else if (ge.type === GameEventType.DAMAGE_TAKEN) {
          this.onDamage(world, ps, now, ge.attacker, ge.amount, ge.dirYaw);
        } else if (ge.type === GameEventType.KILL && ge.victim === me) {
          this.killer = ge.killer;
        }
      }
    }
  }

  private onDamage(world: ClientWorld, ps: PlayerState, now: number, attacker: number, amount: number, dirYaw: number): void {
    let dir: Vec3 | null = null;
    const me = world.localClientNum;
    const a = attacker !== me ? world.getPlayer(attacker) : undefined;
    if (a) {
      // damage_from = direction of travel attacker -> victim (Q3/IW G_Damage dir)
      const d: Vec3 = [world.view.origin[0] - a.origin[0], world.view.origin[1] - a.origin[1], (world.view.origin[2] + world.view.viewHeight) - (a.origin[2] + a.viewHeight)];
      const len = Math.hypot(d[0], d[1], d[2]);
      if (len > 0.001) dir = [d[0] / len, d[1] / len, d[2] / len];
    } else if (attacker !== me && attacker >= 0 && Number.isFinite(dirYaw)) {
      // ESTIMATE: dirYaw = yaw (deg) of the damage direction when the attacker is not visible
      const y = (dirYaw * Math.PI) / 180;
      dir = [Math.cos(y), Math.sin(y), 0];
    }
    this.flinch.hit(now, amount, ps.maxHealth || 100, dir, this.fwd, this.right);
  }

  private firstPerson(world: ClientWorld, ps: PlayerState, def: WeaponDef | null, dtMs: number, now: number, r: RefDef): void {
    const v = world.view;
    // cg.xyspeed: ladder uses vertical speed (CG_CalcViewValues). Velocity and bobCycle are lerped to render rate
    // like v.origin (psview PredictedLerp): T6 advances them every client frame.
    const vel = predictedLerp.velocity;
    const xyspeed = ps.pm_flags & PMF.LADDER ? Math.abs(vel[2]) : Math.hypot(vel[0], vel[1]);
    r.origin[0] = v.origin[0]; r.origin[1] = v.origin[1]; r.origin[2] = v.origin[2];
    r.origin[2] += this.step.offset(now); // CG_SmoothCameraZ
    r.angles[0] = v.viewangles[0]; r.angles[1] = v.viewangles[1]; r.angles[2] = v.viewangles[2];
    // BG_CalculateViewMovementAngles: DamageKick, (Idle/Bob only for overlay reticles: none in SnS), AdsBob
    this.flinch.applyView(now, ps.adsFrac, r.angles);
    const bobPs = ps; // PlayerState carries every BobPs field
    const cycle = getBobCycle(predictedLerp.bobCycle);
    if (def && ps.adsFrac > 0) {
      const mult = rawNum(def, 'adsViewBobMult');
      if (mult > 0) {
        const k = ps.adsFrac * mult;
        r.angles[0] -= viewBobVertical(bobPs, cycle, xyspeed) * k;
        r.angles[1] -= viewBobHorizontal(bobPs, cycle, xyspeed) * k;
      }
    }
    r.origin[2] += v.viewHeight;
    r.origin[2] += viewBobVertical(bobPs, cycle, xyspeed);
    const hb = viewBobHorizontal(bobPs, cycle, xyspeed);
    angleVectors(r.angles, null, this.right, null);
    r.origin[0] += hb * this.right[0]; r.origin[1] += hb * this.right[1]; r.origin[2] += hb * this.right[2];
    r.origin[2] += this.land.view(now);
    if (v.origin[2] + 8 > r.origin[2]) r.origin[2] = v.origin[2] + 8;
    // fire kick (CG_KickAngles runs every frame)
    this.kick.update(vmDebug.paused ? 0 : dtMs, def ? kickDef(def) : null, ps.adsFrac);
    r.angles[0] += this.kick.angles[0]; r.angles[1] += this.kick.angles[1]; r.angles[2] += this.kick.angles[2];
    this.shakes.apply(now, r.origin, r.angles);
  }

  private deathCam(world: ClientWorld, ps: PlayerState, r: RefDef): void {
    const v = world.view;
    if (this.deadYaw === null) {
      const k = this.killer >= 0 && this.killer !== world.localClientNum ? world.getPlayer(this.killer) : undefined;
      this.deadYaw = k ? (Math.atan2(k.origin[1] - v.origin[1], k.origin[0] - v.origin[0]) * 180) / Math.PI : v.viewangles[1];
    }
    const focus: Vec3 = [v.origin[0], v.origin[1], v.origin[2] + DEATHCAM_FOCUS_HEIGHT];
    let fp = v.viewangles[0];
    if (fp > 45) fp = 45;
    const fwd: Vec3 = [0, 0, 0];
    angleVectors([fp, this.deadYaw, 0], fwd, null, null);
    const point: Vec3 = [focus[0] + CG_THIRDPERSON_FOCUS_DIST * fwd[0], focus[1] + CG_THIRDPERSON_FOCUS_DIST * fwd[1], focus[2] + CG_THIRDPERSON_FOCUS_DIST * fwd[2]];
    const fa: Vec3 = [v.viewangles[0] * 0.5, this.deadYaw - CG_THIRDPERSON_ANGLE, 0];
    angleVectors(fa, fwd, null, null);
    r.origin[0] = focus[0] - CG_THIRDPERSON_RANGE * fwd[0];
    r.origin[1] = focus[1] - CG_THIRDPERSON_RANGE * fwd[1];
    r.origin[2] = focus[2] + 8 - CG_THIRDPERSON_RANGE * fwd[2];
    // wall check (CG_OffsetThirdPersonView / ThirdPersonViewTrace, deathcam.ts): without it the camera sat behind
    // walls and door frames 120 u behind the corpse
    thirdPersonViewTrace(this.collision, focus, r.origin);
    const dx = point[0] - r.origin[0], dy = point[1] - r.origin[1], dz = point[2] - r.origin[2];
    const dist = Math.max(1, Math.hypot(dx, dy));
    r.angles[0] = (-Math.atan2(dz, dist) * 180) / Math.PI;
    r.angles[1] = fa[1];
    r.angles[2] = 0;
  }
}
